<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="wallet-fill" class="title-icon"></b-icon>
          {{ mode === 'employee' ? 'Xodimlar' : 'Ustalar' }} Balansi va To'lov
        </h2>
        <div class="header-actions">
          <button class="mode-btn" :class="{ active: mode === 'employee' }" @click="setMode('employee')">
            <b-icon icon="people-fill"></b-icon> Xodimlar
          </button>
          <button class="mode-btn" :class="{ active: mode === 'master' }" @click="setMode('master')">
            <b-icon icon="tools"></b-icon> Ustalar
          </button>
          <button class="refresh-btn" @click="loadList()">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
        </div>
      </div>

      <!-- FILTER -->
      <div class="filter-panel">
        <div class="filter-grid">
          <div v-if="mode === 'employee'" class="filter-cell">
            <label>Sklad</label>
            <select v-model="filters.sklad_id" class="form-input" @change="loadList()">
              <option value="">Joriy sklad</option>
              <option value="all">Barcha skladlar</option>
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="filter-cell">
            <label>Sanadan</label>
            <input type="date" v-model="filters.date1" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>Sanagacha</label>
            <input type="date" v-model="filters.date2" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>&nbsp;</label>
            <div class="action-row">
              <button class="btn-primary" @click="loadList()" :disabled="loading">
                <b-icon icon="search"></b-icon> Hisoblash
              </button>
              <button class="btn-secondary" @click="quickRange('today')">Bugun</button>
              <button class="btn-secondary" @click="quickRange('week')">Hafta</button>
              <button class="btn-secondary" @click="quickRange('month')">Bu oy</button>
              <button class="btn-secondary" @click="quickRange('alltime')">Boshidan</button>
            </div>
          </div>
        </div>
      </div>

      <!-- TOTALS -->
      <div v-if="totals" class="totals-row">
        <div class="t-card success">
          <div class="t-label">Sof KPI</div>
          <div class="t-value">{{ formatMoney(totals.kpi_net) }}</div>
          <div class="t-sub">{{ formatMoney(totals.kpi_accrued) }} − {{ formatMoney(totals.kpi_reversed) }}</div>
        </div>
        <div v-if="mode === 'employee' && totals.daily_accrued > 0" class="t-card warning">
          <div class="t-label">Oylik kerak</div>
          <div class="t-value">{{ formatMoney(totals.daily_accrued) }}</div>
          <div class="t-sub">davr ichidagi kunlik</div>
        </div>
        <div class="t-card info">
          <div class="t-label">Jami kerak</div>
          <div class="t-value">{{ formatMoney(totals.kpi_net + (totals.daily_accrued || 0)) }}</div>
          <div class="t-sub">KPI + oylik</div>
        </div>
        <div class="t-card primary">
          <div class="t-label">To'langan</div>
          <div class="t-value">{{ formatMoney(totals.salary_paid) }}</div>
        </div>
        <div class="t-card" :class="totals.balance >= 0 ? 'success-strong' : 'danger'">
          <div class="t-label">Joriy balans</div>
          <div class="t-value">{{ formatMoney(totals.balance) }}</div>
          <div class="t-sub">{{ totals.balance >= 0 ? 'kompaniya qarzi' : "xodim qarzi" }}</div>
        </div>
      </div>

      <!-- TABLE — XODIM (employee) -->
      <div v-if="mode === 'employee'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Xodim</th>
                <th class="num">Kunlik oylik</th>
                <th class="num">KPI %</th>
                <th class="num">Savdolar</th>
                <th class="num">Sof KPI</th>
                <th class="num">Oylik kerak<br><small style="font-weight:500;color:#94a3b8">{{ items[0] ? items[0].period_days + ' kun' : '' }}</small></th>
                <th class="num">Jami kerak</th>
                <th class="num">To'langan</th>
                <th class="num">Balans</th>
                <th>Holat</th>
                <th style="width:140px;text-align:center">Amallar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in items" :key="r.user_id">
                <td>{{ i + 1 }}</td>
                <td class="name-cell">
                  <div class="user-name">{{ r.first_name }} {{ r.last_name }}</div>
                  <div class="user-username">@{{ r.username }}</div>
                </td>
                <td class="num muted">{{ formatMoney(r.daily_salary) }}</td>
                <td class="num">
                  <span class="kpi-pct">{{ r.kpi_percent }}%</span>
                </td>
                <td class="num"><span class="count-badge">{{ r.sales_count }}</span></td>
                <td class="num bold" :class="r.kpi_net >= 0 ? 'positive' : 'negative'"
                    :title="'Hisoblangan: ' + formatMoney(r.kpi_accrued) + (r.kpi_reversed > 0 ? '\nQaytarilgan: −' + formatMoney(r.kpi_reversed) : '')">
                  {{ formatMoney(r.kpi_net) }}
                </td>
                <td class="num positive">{{ formatMoney(r.daily_accrued) }}</td>
                <td class="num bold" :title="'Sof KPI + Oylik kerak'">
                  {{ formatMoney(r.kpi_net + r.daily_accrued) }}
                </td>
                <td class="num">{{ formatMoney(r.salary_paid) }}</td>
                <td class="num bold big" :class="balanceClass(r.balance)">
                  {{ formatMoney(r.balance) }}
                </td>
                <td>
                  <span class="status-badge" :class="'status-' + r.status">
                    {{ statusLabel(r.status) }}
                  </span>
                </td>
                <td style="text-align:center">
                  <button class="icon-btn" @click="openHistory(r)" title="Tarixi">
                    <b-icon icon="clock-history"></b-icon>
                  </button>
                  <button class="icon-btn pay" @click="openPaySuggest(r)" title="To'lash">
                    <b-icon icon="cash-stack"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="items.length === 0 && !loading">
                <td colspan="12" class="empty-row">Ma'lumot topilmadi</td>
              </tr>
              <tr v-if="loading">
                <td colspan="12" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TABLE — USTA (master) -->
      <div v-if="mode === 'master'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Usta</th>
                <th>Lavozim</th>
                <th class="num">Kunlik oylik</th>
                <th class="num">Bonus %</th>
                <th class="num">Savdolar</th>
                <th class="num">Sof bonus</th>
                <th class="num">Oylik kerak<br><small style="font-weight:500;color:#94a3b8">{{ masterItems[0] ? masterItems[0].period_days + ' kun' : '' }}</small></th>
                <th class="num">Jami kerak</th>
                <th class="num">To'langan</th>
                <th class="num">Balans</th>
                <th>Holat</th>
                <th style="width:140px;text-align:center">Amallar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in masterItems" :key="r.staff_id">
                <td>{{ i + 1 }}</td>
                <td class="name-cell">
                  <div class="user-name">{{ r.name }}</div>
                  <div class="user-username">{{ r.phone_number || '—' }}</div>
                </td>
                <td>{{ r.position || '—' }}</td>
                <td class="num muted">{{ formatMoney(r.base_salary) }}</td>
                <td class="num">
                  <span class="kpi-pct">{{ r.bonus_percent }}%</span>
                </td>
                <td class="num"><span class="count-badge">{{ r.sales_count }}</span></td>
                <td class="num bold" :class="r.bonus_net >= 0 ? 'positive' : 'negative'"
                    :title="'Hisoblangan: ' + formatMoney(r.bonus_accrued) + (r.bonus_reversed > 0 ? '\nQaytarilgan: −' + formatMoney(r.bonus_reversed) : '')">
                  {{ formatMoney(r.bonus_net) }}
                </td>
                <td class="num positive">{{ formatMoney(r.daily_owed || 0) }}</td>
                <td class="num bold">{{ formatMoney(r.bonus_net + (r.daily_owed || 0)) }}</td>
                <td class="num">{{ formatMoney(r.bonus_paid) }}</td>
                <td class="num bold big" :class="balanceClass(r.balance)">
                  {{ formatMoney(r.balance) }}
                </td>
                <td>
                  <span class="status-badge" :class="masterStatusClass(r.status)">
                    {{ masterStatusLabel(r.status) }}
                  </span>
                </td>
                <td style="text-align:center">
                  <button class="icon-btn" @click="openMasterHistory(r)" title="Tarixi">
                    <b-icon icon="clock-history"></b-icon>
                  </button>
                  <button class="icon-btn pay" @click="openMasterPaySuggest(r)" title="To'lash">
                    <b-icon icon="cash-stack"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="masterItems.length === 0 && !loading">
                <td colspan="13" class="empty-row">Ustalar mavjud emas yoki savdo qilmagan</td>
              </tr>
              <tr v-if="loading">
                <td colspan="13" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- HISTORY MODAL -->
    <b-modal id="modal-history" hide-footer size="xl" modal-class="modern-modal">
      <template #modal-title>
        <span v-if="historyData">
          <b-icon icon="clock-history"></b-icon>
          {{ historyData.user.first_name }} {{ historyData.user.last_name }} — balans tarixi
        </span>
      </template>

      <div v-if="historyData" class="history-content">
        <div class="hist-summary">
          <div class="hist-card success">
            <span class="hist-label">KPI hisoblangan</span>
            <span class="hist-val">{{ formatMoney(historyData.summary.kpi_accrued) }}</span>
          </div>
          <div class="hist-card danger">
            <span class="hist-label">KPI qaytarilgan</span>
            <span class="hist-val">−{{ formatMoney(historyData.summary.kpi_reversed) }}</span>
          </div>
          <div class="hist-card info">
            <span class="hist-label">Sof KPI</span>
            <span class="hist-val">{{ formatMoney(historyData.summary.kpi_net) }}</span>
          </div>
          <div class="hist-card primary">
            <span class="hist-label">Oylik to'langan</span>
            <span class="hist-val">{{ formatMoney(historyData.summary.salary_paid) }}</span>
          </div>
          <div class="hist-card" :class="historyData.summary.balance >= 0 ? 'success-strong' : 'danger'">
            <span class="hist-label">Joriy balans</span>
            <span class="hist-val big">{{ formatMoney(historyData.summary.balance) }}</span>
          </div>
        </div>

        <h5 class="section-title">📜 Tranzaktsiyalar</h5>
        <div class="hist-table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sana</th>
                <th>Turi</th>
                <th>Hujjat #</th>
                <th class="num">Summa</th>
                <th class="num">O'zgarish</th>
                <th class="num">Balans</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t, i) in historyData.items" :key="t.id">
                <td>{{ i + 1 }}</td>
                <td class="muted">{{ formatDate(t.datetime) }}</td>
                <td>
                  <span class="txn-tag" :class="'tag-' + tagClass(t.doc_type)">
                    {{ t.label }}
                  </span>
                </td>
                <td class="muted">#{{ t.doc_id }}</td>
                <td class="num">{{ formatMoney(t.summa) }}</td>
                <td class="num bold" :class="t.delta >= 0 ? 'positive' : 'negative'">
                  {{ t.delta >= 0 ? '+' : '' }}{{ formatMoney(t.delta) }}
                </td>
                <td class="num bold" :class="balanceClass(t.running_balance)">
                  {{ formatMoney(t.running_balance) }}
                </td>
              </tr>
              <tr v-if="historyData.items.length === 0">
                <td colspan="7" class="empty-row">Tranzaktsiyalar yo'q</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- PAY SUGGEST MODAL -->
    <b-modal id="modal-pay" hide-footer size="lg" modal-class="modern-modal">
      <template #modal-title>
        <span v-if="paySuggest">
          <b-icon icon="cash-stack"></b-icon>
          {{ paySuggest.user.first_name }} {{ paySuggest.user.last_name }} — to'lov tavsiyasi
        </span>
      </template>

      <div v-if="paySuggest" class="pay-content">
        <!-- Salary type info -->
        <div v-if="paySuggest.user.salary_type" class="salary-type-info">
          <span v-if="paySuggest.user.salary_type === 'oylik'">
            🗓️ Oylik xodim — har 30 kunga {{ formatMoney(paySuggest.user.daily_salary) }}
          </span>
          <span v-else>
            📅 Kunlik xodim — har kunga {{ formatMoney(paySuggest.user.daily_salary) }}
          </span>
        </div>

        <!-- Days input -->
        <div class="form-unit days-unit">
          <label>{{ paySuggest.user.salary_type === 'oylik' ? "Necha kunga (30 = bitta oy)" : "Necha kunlik oylik?" }}</label>
          <div class="days-quick">
            <input
              type="number"
              v-model.number="payDays"
              min="0" max="365"
              class="form-input days-input"
              @change="reloadPaySuggest()"
            />
            <button v-if="paySuggest.user.salary_type === 'kunlik'" class="day-quick-btn" @click="setDaysAndReload(1)">1 kun</button>
            <button v-if="paySuggest.user.salary_type === 'kunlik'" class="day-quick-btn" @click="setDaysAndReload(7)">Hafta</button>
            <button class="day-quick-btn" @click="setDaysAndReload(30)">Oy</button>
          </div>
        </div>

        <!-- Breakdown -->
        <div class="pay-breakdown">
          <div class="pb-row">
            <span v-if="paySuggest.user.salary_type === 'oylik'">
              ({{ paySuggest.days }}/30) × {{ formatMoney(paySuggest.user.daily_salary) }} (oylik)
            </span>
            <span v-else>
              {{ paySuggest.days }} kun × {{ formatMoney(paySuggest.user.daily_salary) }}
            </span>
            <span class="bold">{{ formatMoney(paySuggest.base_salary) }}</span>
          </div>
          <div class="pb-row positive">
            <span>+ KPI bonus ({{ paySuggest.user.kpi_percent }}%)</span>
            <span class="bold">+{{ formatMoney(paySuggest.kpi_accrued) }}</span>
          </div>
          <div v-if="paySuggest.kpi_reversed > 0" class="pb-row negative">
            <span>− KPI qaytarilgan</span>
            <span class="bold">−{{ formatMoney(paySuggest.kpi_reversed) }}</span>
          </div>
          <div v-if="paySuggest.salary_paid > 0" class="pb-row negative">
            <span>− Oldindan to'langan oyliklar</span>
            <span class="bold">−{{ formatMoney(paySuggest.salary_paid) }}</span>
          </div>
          <div class="pb-row middle" :class="paySuggest.current_balance >= 0 ? 'positive' : 'negative'">
            <span><b>Joriy balans:</b></span>
            <span class="bold big">{{ formatMoney(paySuggest.current_balance) }}</span>
          </div>
          <div v-if="paySuggest.carry_over > 0" class="pb-warning">
            ⚠️ Xodim {{ formatMoney(paySuggest.carry_over) }} qarzdor — keyingi savdolardan ushlab qolinadi
          </div>
          <div class="pb-row final" :class="paySuggest.total_to_pay > 0 ? 'positive' : 'negative'">
            <span><b>JAMI TO'LASH KERAK:</b></span>
            <span class="bold big">{{ formatMoney(paySuggest.total_to_pay) }}</span>
          </div>
        </div>

        <!-- Pay form -->
        <div class="pay-form">
          <div class="form-unit">
            <label>Aslida to'lanadigan summa</label>
            <input
              type="number"
              v-model.number="payAmount"
              :placeholder="paySuggest.total_to_pay"
              class="form-input"
            />
            <small class="hint">Tavsiya: {{ formatMoney(paySuggest.total_to_pay) }}</small>
          </div>
          <div class="form-unit">
            <label>Izoh</label>
            <input type="text" v-model="payComment" class="form-input" placeholder="Ixtiyoriy" />
          </div>
          <button class="btn-primary big-btn" @click="submitPayment()" :disabled="paying || !payAmount">
            <b-icon icon="check-all"></b-icon>
            {{ paying ? 'Saqlanmoqda...' : "To'lov saqlash" }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'EmployeeBalance',
  data() {
    return {
      mode: 'employee',  // 'employee' | 'master'
      loading: false,
      paying: false,
      sklads: [],
      filters: {
        sklad_id: '',
        date1: new Date().toISOString().slice(0, 10),
        date2: new Date().toISOString().slice(0, 10),
      },
      items: [],         // employee items
      masterItems: [],   // master items
      totals: null,

      historyData: null,
      paySuggest: null,
      payDays: 30,
      payAmount: null,
      payComment: '',
      paySubject: 'employee', // 'employee' | 'master' — to'lov kim uchun
      currentUserId: null,
      currentMasterId: null,
    };
  },
  methods: {
    today() { return new Date().toISOString().slice(0, 10); },
    firstDayOfMonth() {
      const d = new Date();
      d.setDate(1);
      return d.toISOString().slice(0, 10);
    },
    toUnix(s, end = false) {
      if (!s) return null;
      const d = new Date(s);
      if (end) d.setHours(23, 59, 59, 999);
      else d.setHours(0, 0, 0, 0);
      return Math.floor(d.getTime() / 1000);
    },
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatDate(unix) {
      if (!unix) return '—';
      const d = new Date(unix * 1000);
      return d.toLocaleDateString('uz-UZ') + ' ' + d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
    },
    balanceClass(n) {
      const v = parseFloat(n || 0);
      if (v > 0)  return 'positive';
      if (v < 0)  return 'negative';
      return 'muted';
    },
    statusLabel(s) {
      return {
        company_owes: '🟢 Beriladi',
        employee_owes: '🔴 Qarzdor',
        settled: '⚪ Tenglik',
      }[s] || s;
    },
    masterStatusLabel(s) {
      return {
        company_owes: '🟢 Beriladi',
        master_owes:  '🔴 Qarzdor',
        settled:      '⚪ Tenglik',
      }[s] || s;
    },
    masterStatusClass(s) {
      return {
        company_owes: 'status-company_owes',
        master_owes:  'status-employee_owes',
        settled:      'status-settled',
      }[s] || '';
    },
    setMode(m) {
      this.mode = m;
      this.items = [];
      this.masterItems = [];
      this.totals = null;
      this.loadList();
    },
    tagClass(docType) {
      if (docType === 'KPI bonus')      return 'kpi-add';
      if (docType === 'KPI qaytarish')  return 'kpi-rev';
      if (docType === 'Маош')            return 'salary';
      if (docType === 'Kunlik oylik')   return 'daily';
      return 'other';
    },
    quickRange(t) {
      const now = new Date();
      const fmt = (d) => d.toISOString().slice(0, 10);
      if (t === 'today') {
        this.filters.date1 = fmt(now);
        this.filters.date2 = fmt(now);
      } else if (t === 'week') {
        const d = new Date();
        d.setDate(d.getDate() - 6);
        this.filters.date1 = fmt(d);
        this.filters.date2 = fmt(now);
      } else if (t === 'month') {
        this.filters.date1 = this.firstDayOfMonth();
        this.filters.date2 = fmt(now);
      } else if (t === 'alltime') {
        this.filters.date1 = '';
        this.filters.date2 = '';
      }
      this.loadList();
    },

    async loadList() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.date1) params.date1 = this.toUnix(this.filters.date1);
        if (this.filters.date2) params.date2 = this.toUnix(this.filters.date2, true);

        if (this.mode === 'employee') {
          if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
          const res = await axios.get('api/v1/report/employee-balance', { params });
          this.items = res.data.items || [];
          this.totals = this.normalizeEmployeeTotals(res.data.totals);
        } else {
          // master
          const res = await axios.get('api/v1/report/master-balance', { params });
          this.masterItems = res.data.items || [];
          this.totals = this.normalizeMasterTotals(res.data.totals);
        }
      } finally {
        this.loading = false;
      }
    },
    normalizeEmployeeTotals(t) {
      if (!t) return null;
      return {
        kpi_accrued: t.kpi_accrued,
        kpi_reversed: t.kpi_reversed,
        kpi_net: t.kpi_net,
        daily_accrued: t.daily_accrued || 0,
        salary_paid: t.salary_paid,
        balance: t.balance,
      };
    },
    normalizeMasterTotals(t) {
      if (!t) return null;
      return {
        kpi_accrued: t.bonus_accrued,
        kpi_reversed: t.bonus_reversed,
        kpi_net: t.bonus_net,
        daily_accrued: t.daily_owed || 0,
        salary_paid: t.bonus_paid,
        balance: t.balance,
      };
    },

    async openHistory(row) {
      this.historyData = null;
      this.$bvModal.show('modal-history');
      const params = { limit: 200 };
      if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
      if (this.filters.date1)    params.date1 = this.toUnix(this.filters.date1);
      if (this.filters.date2)    params.date2 = this.toUnix(this.filters.date2, true);
      const res = await axios.get(`api/v1/report/employee-balance/${row.user_id}`, { params });
      this.historyData = res.data;
    },

    async openPaySuggest(row) {
      this.paySubject = 'employee';
      this.paySuggest = null;
      this.payAmount = null;
      this.payComment = '';
      // salary_type ga qarab default kunlar:
      //   kunlik xodim → 1 kun
      //   oylik xodim  → 30 kun
      this.payDays = (row.salary_type === 'oylik') ? 30 : 1;
      this.currentUserId = row.user_id;
      this.$bvModal.show('modal-pay');
      await this.loadPaySuggest(row.user_id);
    },

    async loadPaySuggest(user_id) {
      this.currentUserId = user_id;
      const res = await axios.get(`api/v1/report/employee-payment-suggest/${user_id}`, {
        params: { days: this.payDays }
      });
      this.paySuggest = res.data;
      // Backend default_days yuborsa, agar foydalanuvchi hali kiritmagan bo'lsa moslaymiz
      if (res.data.default_days && this.payDays === 30 && res.data.user.salary_type === 'kunlik') {
        this.payDays = res.data.default_days;
      }
      if (this.payAmount === null) this.payAmount = res.data.total_to_pay;
    },

    async reloadPaySuggest() {
      // payDays o'zgarganda — paySubject'ga qarab to'g'ri endpointni qayta chaqiramiz
      this.payAmount = null;
      if (this.paySubject === 'master') {
        await this.loadMasterPaySuggest(this.currentMasterId);
      } else {
        await this.loadPaySuggest(this.currentUserId);
      }
    },

    async setDaysAndReload(days) {
      this.payDays = days;
      await this.reloadPaySuggest();
    },

    async openMasterHistory(row) {
      this.historyData = null;
      this.$bvModal.show('modal-history');
      const params = { limit: 200 };
      if (this.filters.date1) params.date1 = this.toUnix(this.filters.date1);
      if (this.filters.date2) params.date2 = this.toUnix(this.filters.date2, true);
      const res = await axios.get(`api/v1/report/master-balance/${row.staff_id}`, { params });
      // Master javobini employee formatiga keltirish
      const data = res.data;
      this.historyData = {
        user: {
          first_name: data.staff.name,
          last_name:  '',
          username:   data.staff.position || '',
          kpi_percent: data.staff.bonus_percent,
          daily_salary: data.staff.base_salary,
        },
        count: data.count,
        items: data.items.map(t => ({
          id: t.id,
          datetime: Math.floor(new Date(t.created_at).getTime() / 1000),
          doc_id: t.id,
          doc_type: t.label,
          label: t.label,
          summa: t.summa,
          delta: t.delta,
          running_balance: t.balance,
          sklad: { id: 0, name: '—' },
        })),
        summary: {
          kpi_accrued:  data.summary.bonus_accrued,
          kpi_reversed: data.summary.bonus_reversed,
          kpi_net:      data.summary.bonus_net,
          salary_paid:  data.summary.bonus_paid,
          balance:      data.summary.balance,
        },
      };
    },

    async openMasterPaySuggest(row) {
      this.paySubject = 'master';
      this.paySuggest = null;
      this.payAmount = null;
      this.payComment = '';
      this.payDays = 30;
      this.currentMasterId = row.staff_id;
      this.$bvModal.show('modal-pay');
      await this.loadMasterPaySuggest(row.staff_id);
    },

    async loadMasterPaySuggest(staff_id) {
      const res = await axios.get(`api/v1/report/master-payment-suggest/${staff_id}`, {
        params: { days: this.payDays }
      });
      const d = res.data;
      // Master javobini employee struktura ko'rinishiga keltirish
      this.paySuggest = {
        user: {
          id: d.staff.id,
          first_name: d.staff.name,
          last_name: '',
          kpi_percent: d.staff.bonus_percent,
          daily_salary: d.staff.daily_salary,
        },
        days: d.days,
        base_salary: d.base_salary,
        kpi_accrued: d.bonus_accrued,
        kpi_reversed: d.bonus_reversed,
        kpi_net: d.bonus_net,
        salary_paid: d.bonus_paid,
        current_balance: d.current_balance,
        carry_over: d.carry_over,
        total_to_pay: d.total_to_pay,
      };
      if (this.payAmount === null) this.payAmount = d.total_to_pay;
    },

    async submitPayment() {
      if (!this.paySuggest || !this.payAmount || this.payAmount <= 0) return;
      const confirmText = `${this.formatMoney(this.payAmount)} ${this.paySuggest.user.first_name} ${this.paySuggest.user.last_name || ''} ga to'lansinmi?`;
      if (!confirm(confirmText)) return;

      this.paying = true;
      try {
        if (this.paySubject === 'master') {
          // Master uchun user_pay endpointini chaqiramiz
          await axios.post('api/v1/user_pay', {
            datetime: Math.floor(Date.now() / 1000),
            staff_id: this.currentMasterId,
            summa: this.payAmount,
            pay_type_id: 1, // Naqd default
            sklad_id: this.filters.sklad_id && this.filters.sklad_id !== 'all'
                      ? parseInt(this.filters.sklad_id)
                      : parseInt(localStorage.getItem('sklad_id')),
            reason: this.payComment || 'Oylik + bonus',
          });
        } else {
          // Xodim uchun salary documenti
          await axios.post('api/v1/salary', {
            created_at: Math.floor(Date.now() / 1000),
            updated_at: Math.floor(Date.now() / 1000),
            sklad_id: this.filters.sklad_id && this.filters.sklad_id !== 'all'
                      ? parseInt(this.filters.sklad_id)
                      : parseInt(localStorage.getItem('sklad_id')),
            summa: this.payAmount,
            count_all: 1,
            comment: this.payComment || `Avtomatik: ${this.paySuggest.days} kun + KPI`,
            dollar_rate: 1,
            table: [{
              user_id: this.paySuggest.user.id,
              summa:   this.payAmount,
            }],
          });
        }
        this.$bvModal.hide('modal-pay');
        await this.loadList();
        alert("To'lov muvaffaqiyatli saqlandi");
      } catch (e) {
        console.error(e);
        alert("Xatolik: " + (e.response?.data?.message || e.message));
      } finally {
        this.paying = false;
      }
    },

    loadSklads() {
      axios.get('api/v1/sklads').then(r => { this.sklads = r.data || []; }).catch(() => {});
    },
  },
  mounted() {
    this.loadSklads();
    this.loadList();
  },
};
</script>

<style scoped>
.g-page { padding: 20px; height: 100%; overflow-y: auto; background: #f8fafc; }
.content-container { max-width: 1500px; margin: 0 auto; }

.header-row {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 16px; flex-wrap: wrap; gap: 12px;
}
.page-title {
  display: flex; align-items: center; gap: 10px;
  font-size: 22px; font-weight: 800; color: #1e293b; margin: 0;
}
.title-icon { color: #16a34a; font-size: 24px; }
.refresh-btn {
  background: #f1f5f9; border: none; padding: 8px 16px;
  border-radius: 10px; cursor: pointer; color: #64748b;
  display: flex; align-items: center; gap: 6px; font-weight: 600;
}
.refresh-btn:hover { background: #e2e8f0; }

.mode-btn {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 7px 16px;
  border-radius: 10px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: 0.2s;
  font-size: 13px;
}
.mode-btn.active {
  border-color: #16a34a;
  color: #15803d;
  background: #f0fdf4;
  box-shadow: 0 2px 8px rgba(22,163,74,0.15);
}

/* FILTER */
.filter-panel {
  background: white; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 16px; margin-bottom: 14px;
}
.filter-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.filter-cell label {
  display: block; font-size: 11px; font-weight: 700;
  color: #94a3b8; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.form-input {
  width: 100%; background: #f8fafc;
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 9px 12px; font-size: 14px;
  color: #1e293b; outline: none; transition: 0.2s;
}
.form-input:focus { border-color: #16a34a; background: white; }
.action-row { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-primary {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white; border: none;
  padding: 9px 18px; border-radius: 10px;
  font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(22,163,74,0.25);
}
.btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
.btn-secondary {
  background: #f1f5f9; color: #334155; border: none;
  padding: 9px 14px; border-radius: 10px;
  font-weight: 600; font-size: 13px; cursor: pointer;
}
.btn-secondary:hover { background: #e2e8f0; }
.big-btn { width: 100%; padding: 14px; justify-content: center; font-size: 15px; }

/* TOTALS */
.totals-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px; margin-bottom: 14px;
}
.t-card {
  background: white; border-radius: 12px;
  padding: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid #94a3b8;
}
.t-card.success { border-color: #22c55e; }
.t-card.success-strong { border-color: #16a34a; background: #f0fdf4; }
.t-card.warning { border-color: #f59e0b; }
.t-card.info    { border-color: #06b6d4; }
.t-card.primary { border-color: #2563eb; }
.t-card.danger  { border-color: #ef4444; background: #fef2f2; }
.t-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.t-value { font-size: 18px; font-weight: 800; color: #1e293b; margin: 4px 0 2px 0; font-family: 'Inter', system-ui; }
.t-sub   { font-size: 11px; color: #64748b; }

/* TABLE */
.table-card {
  background: white; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  overflow: hidden;
}
.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table thead { position: sticky; top: 0; z-index: 2; }
.custom-table th {
  background: #f8fafc; padding: 11px 12px;
  color: #94a3b8; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap; text-align: left;
}
.custom-table th.num,
.custom-table td.num { text-align: right; }
.custom-table td {
  padding: 10px 12px; border-bottom: 1px solid #f8fafc;
  font-size: 13px; color: #334155; vertical-align: middle;
}
.bold  { font-weight: 800; }
.big   { font-size: 14px; }
.muted { color: #94a3b8; }
.positive { color: #16a34a; font-weight: 700; }
.negative { color: #dc2626; font-weight: 700; }

.name-cell .user-name { font-weight: 700; color: #1e293b; font-size: 13px; }
.name-cell .user-username { font-size: 11px; color: #94a3b8; }

.kpi-pct {
  background: #f0fdf4; color: #15803d;
  padding: 3px 10px; border-radius: 12px;
  font-size: 12px; font-weight: 700;
}
.count-badge {
  background: #eff6ff; color: #1d4ed8;
  padding: 3px 10px; border-radius: 12px;
  font-size: 12px; font-weight: 700;
}

.status-badge {
  display: inline-block; padding: 3px 10px;
  border-radius: 12px; font-size: 11px; font-weight: 700;
}
.status-company_owes  { background: #dcfce7; color: #15803d; }
.status-employee_owes { background: #fee2e2; color: #b91c1c; }
.status-settled       { background: #f1f5f9; color: #64748b; }

.icon-btn {
  background: #f1f5f9; border: none;
  width: 30px; height: 30px; border-radius: 8px;
  cursor: pointer; color: #2563eb; margin: 0 2px;
}
.icon-btn:hover { background: #2563eb; color: white; }
.icon-btn.pay { color: #16a34a; }
.icon-btn.pay:hover { background: #16a34a; color: white; }

.empty-row { text-align: center; padding: 50px !important; color: #94a3b8; font-style: italic; }
.loading-row { text-align: center; padding: 30px; color: #16a34a; }
.spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid #dcfce7; border-top-color: #16a34a;
  border-radius: 50%; animation: spin 0.7s linear infinite;
  vertical-align: middle; margin-right: 6px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* HISTORY */
.history-content { padding: 5px; }
.hist-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px; margin-bottom: 16px;
}
.hist-card {
  background: white; padding: 12px 14px; border-radius: 10px;
  border-left: 3px solid #94a3b8;
  display: flex; flex-direction: column;
}
.hist-card.success { border-color: #22c55e; }
.hist-card.success-strong { border-color: #16a34a; background: #f0fdf4; }
.hist-card.danger { border-color: #dc2626; background: #fef2f2; }
.hist-card.info { border-color: #06b6d4; }
.hist-card.primary { border-color: #2563eb; }
.hist-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.hist-val { font-size: 16px; font-weight: 800; color: #1e293b; margin-top: 4px; font-family: 'Inter', system-ui; }
.hist-val.big { font-size: 19px; }

.section-title { font-size: 14px; font-weight: 800; color: #334155; margin: 14px 0 10px 0; }

.hist-table-wrapper {
  border: 1px solid #f1f5f9; border-radius: 10px;
  max-height: 50vh; overflow-y: auto;
}

.txn-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;
}
.tag-kpi-add { background: #dcfce7; color: #15803d; }
.tag-kpi-rev { background: #fee2e2; color: #b91c1c; }
.tag-salary  { background: #dbeafe; color: #1d4ed8; }
.tag-daily   { background: #fef3c7; color: #b45309; }
.tag-other   { background: #f1f5f9; color: #64748b; }

/* PAY MODAL */
.pay-content { padding: 5px; }

.days-unit { margin-bottom: 14px; }
.days-input { max-width: 120px; }

.salary-type-info {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #15803d;
  font-weight: 600;
}

.days-quick {
  display: flex;
  gap: 6px;
  align-items: center;
}

.day-quick-btn {
  background: #f1f5f9;
  border: none;
  padding: 7px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  color: #475569;
  transition: 0.2s;
}

.day-quick-btn:hover {
  background: #16a34a;
  color: white;
}

.pay-breakdown {
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
}
.pb-row {
  display: flex; justify-content: space-between;
  padding: 6px 0; font-size: 14px; color: #475569;
}
.pb-row.positive { color: #15803d; }
.pb-row.negative { color: #b91c1c; }
.pb-row.middle {
  border-top: 1px solid #cbd5e1;
  padding-top: 10px; margin-top: 4px;
  font-size: 15px;
}
.pb-row.final {
  border-top: 2px solid #16a34a;
  padding-top: 12px; margin-top: 8px;
  font-size: 18px; font-weight: 800;
}
.pb-row.final.negative { border-color: #dc2626; }
.pb-warning {
  background: #fef3c7; color: #b45309;
  padding: 8px 12px; border-radius: 8px;
  font-size: 12px; font-weight: 600;
  margin: 8px 0;
}

.pay-form {
  background: white;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
}
.form-unit { margin-bottom: 14px; }
.form-unit label {
  display: block; font-size: 11px; font-weight: 700;
  color: #94a3b8; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.hint { font-size: 11px; color: #16a34a; margin-top: 4px; }
</style>
